import React, { ChangeEvent, forwardRef, useImperativeHandle, useState } from 'react'
import InputMask from 'react-input-mask'
import { DefaultTheme } from 'react-jss'
import OutsideClickHandler from 'react-outside-click-handler'
import cn from 'classnames'

import useGlobalStyles from '../../const/globalStyles'
import useStyles from './styles'
import { IInputSMBProps, INPUT_SIZE, INPUT_VARIANT, IResponseProps } from './types'

const Input = forwardRef((props: IInputSMBProps & { theme?: DefaultTheme }, ref: any) => {
  const {
    variant = INPUT_VARIANT.default,
    size = INPUT_SIZE.size40,
    defaultValue,
    value,
    name,
    placeholder = '',
    mask,
    onChange,
    onBlur,
    onFocus,
    onClick,
    inputRef,
    disabled = false,
    type = 'text',
    error = false,
    fullWidth = false,
    rows,
    min,
    max,
    className,
    request,
    suggestionList,
    alwaysShowMask = true,
    leftIcon,
    rightIcon,
    id,
    label,
    errorMessage,
    helperText,
    isOnlyNumbers,
    maxLength,
  } = props

  const classes = useStyles(props)
  const globalClasses = useGlobalStyles()

  const [suggestionResponse, setSuggestionResponse] = useState<IResponseProps | null>(null)

  useImperativeHandle(ref, () => ({
    clearSuggestions: () => {
      setSuggestionResponse(null)
    },
    focus: () => {
      if (ref && ref.current) {
        ref.current.focus()
      }
    },
  }))

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    if (isOnlyNumbers && isNaN(Number(newValue))) {
      return
    }
    if (onChange) {
      onChange(e)
    }

    if (request) {
      request(e.target.value).then((response) => {
        setSuggestionResponse(response as IResponseProps)
      })
    }
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onBlur) {
      onBlur(e)
    }
  }

  const onClearSuggestions = () => {
    setSuggestionResponse(null)
  }

  return (
    <div
      className={cn(
        classes.root,
        classes[variant],
        classes[size],
        {
          [classes.disabled]: disabled,
          [classes.fullWidth]: fullWidth,
          [classes.error]: error,
          [classes.leftIcon]: leftIcon,
          [classes.rightIcon]: rightIcon,
        },
        className
      )}
    >
      {leftIcon && <div className={classes.leftIconWrapper}>{leftIcon}</div>}
      {rightIcon && <div className={classes.rightIconWrapper}>{rightIcon}</div>}
      {mask ? (
        <InputMask
          placeholder={placeholder}
          mask={mask}
          value={defaultValue || value}
          onChange={onChangeHandler}
          onBlur={onBlurHandler}
          onFocus={onFocus}
          onClick={onClick}
          name={name}
          inputRef={inputRef}
          className={classes.input}
          alwaysShowMask={alwaysShowMask}
          id={id}
        />
      ) : rows ? (
        <textarea
          placeholder={placeholder}
          name={name}
          value={value}
          onChange={onChangeHandler}
          onBlur={onBlurHandler}
          onFocus={onFocus}
          onClick={onClick}
          rows={rows}
          disabled={disabled}
          className={classes.input}
          id={id}
        />
      ) : (
        <>
          {label && <div className={classes.label}>{label}</div>}
          <input
            type={type}
            name={name}
            placeholder={placeholder}
            value={defaultValue || value}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onFocus={onFocus}
            onClick={onClick}
            ref={inputRef}
            disabled={disabled}
            className={classes.input}
            min={min}
            max={max}
            maxLength={maxLength}
            id={id}
          />
          {!errorMessage && helperText && <div className={classes.helperText}>{helperText}</div>}
          {errorMessage && <div className={classes.errorMessage}>{errorMessage}</div>}
          {suggestionResponse && suggestionList && (
            <OutsideClickHandler onOutsideClick={onClearSuggestions}>
              <div className={cn(classes.suggestion, globalClasses.scrollbar)}>
                {suggestionList(suggestionResponse)}
              </div>
            </OutsideClickHandler>
          )}
        </>
      )}
    </div>
  )
})

export { Input }
